import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import {
  Briefcase, MapPin, ArrowUpRight, ChevronDown, ChevronUp,
  CircleDot, Building2, RotateCcw,
} from "lucide-react";
import EngagementBadge from "@/components/EngagementBadge";

// Phase 25.16d — Corporate Warm Leads.
// Surfaces RETURNING visitors from corporate-looking ASNs (non-residential,
// non-cloud-egress) at warm or hot engagement. Designed to auto-catch the
// next "TPG Telecom returning 8 times in 6 days" signal so the founder
// doesn't have to spot it manually.
//
// Sits ABOVE HotSessionsPanel on AdminVisitors — narrowest cut, highest
// signal-to-noise.
export default function CorporateWarmLeadsPanel({ days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams({
      days: String(days),
      min_lifetime_pv: "3",
      limit: "20",
    }).toString();
    api.get(`/admin/visitors/warm-leads?${qs}`)
      .then(r => setData(r.data))
      .catch(() => setData({ leads: [], total: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const total = data?.total ?? 0;

  return (
    <Panel
      title="// CORPORATE WARM LEADS · RETURNING + WARM/HOT + NON-RESIDENTIAL"
      testId="corporate-warm-leads-panel"
    >
      <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <Briefcase className="w-3 h-3 text-[#FFB000]" />
          <span className="text-[#FFB000]" data-testid="warm-leads-total">
            {total} {total === 1 ? "lead" : "leads"}
          </span>
          <span className="text-zinc-700">·</span>
          <span className="text-zinc-600">last {days}d · ≥3 lifetime pv · corporate ASN only</span>
        </div>
        <button
          onClick={load}
          data-testid="warm-leads-refresh"
          className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-zinc-700"
        >
          <RotateCcw className={`w-2.5 h-2.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-6 text-center font-mono text-xs text-zinc-600">Loading…</div>
      ) : !data || data.leads.length === 0 ? (
        <div className="p-6 text-center font-mono text-xs text-zinc-600 leading-relaxed max-w-2xl mx-auto">
          No corporate warm leads in the last {days} days. This view auto-fires
          when someone from a workplace ASN (telco-business, enterprise, gov,
          uni) returns 3+ times with warm/hot engagement. Will surface signals
          you would have missed manually.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-900" data-testid="warm-leads-list">
          {data.leads.map((s, idx) => {
            const isOpen = expanded === s.session_id;
            const firstDayEver = (s.returning?.first_seen_ever || s.first_seen || "").slice(0, 10);
            const lastDay = (s.last_seen || "").slice(0, 10);
            const lifetimePv = s.returning?.lifetime_pv ?? s.pageviews;
            const lifetimeDays = s.returning?.lifetime_days ?? 1;
            return (
              <li key={s.session_id} data-testid={`warm-lead-${idx}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : s.session_id)}
                  className="w-full text-left px-4 py-3 hover:bg-zinc-950/40"
                  aria-expanded={isOpen}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-[9px] text-zinc-700 w-6">{String(idx + 1).padStart(2, "0")}</span>
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <Building2 className="w-3 h-3 text-[#FFB000] flex-shrink-0" />
                        <span className="font-mono text-[11px] text-white font-bold truncate">
                          {s.landing_asn_org || "(unknown ASN)"}
                        </span>
                        <EngagementBadge engagement={s.engagement} />
                        {s.landing_asn_city && (
                          <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 flex-shrink-0">
                            <MapPin className="w-2.5 h-2.5" /> {s.landing_asn_city}
                          </span>
                        )}
                        {s.landing_referrer && s.landing_referrer !== "(direct)" && (
                          <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 flex-shrink-0">
                            <ArrowUpRight className="w-2.5 h-2.5" /> {s.landing_referrer}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-mono text-[10px] text-[#FFB000]">
                        {lifetimePv}pv · {lifetimeDays}d
                      </span>
                      {isOpen
                        ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" />
                        : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                    </div>
                  </div>
                  <div className="pl-8 mt-1 font-mono text-[10px] text-zinc-500 truncate">
                    landed on <span className="text-zinc-300">{s.landing_path}</span>
                    {firstDayEver && lastDay && (
                      <> · seen {firstDayEver} → {lastDay}</>
                    )}
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-zinc-900 px-4 py-3 bg-black/40">
                    {s.engagement?.signals?.length > 0 && (
                      <div className="mb-2 flex items-center gap-1.5 flex-wrap">
                        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">Why warm:</span>
                        {s.engagement.signals.map((sig, j) => (
                          <span
                            key={j}
                            className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] border border-[#FFB000]/30 px-1.5 py-px"
                          >
                            {sig}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-2">
                      Journey · this session
                    </div>
                    <ol className="space-y-1.5">
                      {s.journey.map((step, j) => (
                        <li key={j} className="flex items-center gap-2 font-mono text-[11px] text-zinc-400">
                          <CircleDot className="w-2.5 h-2.5 text-[#FFB000] flex-shrink-0" />
                          <span className="truncate">{step.path}</span>
                          {step.referrer && step.referrer !== "(direct)" && (
                            <span className="text-zinc-700 text-[10px]">← {step.referrer}</span>
                          )}
                          <span className="text-zinc-700 ml-auto">{(step.created_at || "").slice(11, 19)}Z</span>
                        </li>
                      ))}
                    </ol>
                    <div className="mt-3 pt-3 border-t border-zinc-900 font-mono text-[9px] uppercase tracking-widest text-zinc-700 break-all">
                      ASN {s.landing_asn || "—"} · session_id: {s.session_id}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
